import {
  BorderOutlined,
  InfoCircleOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useState } from "react";

const { Text } = Typography;
const { Option } = Select;

const UOM_OPTIONS = [
  { label: "Unit(s)", value: "unit", category: "Count" },
  { label: "Dozen", value: "dozen", category: "Count" },
  { label: "Piece (pcs)", value: "pcs", category: "Count" },
  { label: "Box", value: "box", category: "Packaging" },
  { label: "Carton", value: "carton", category: "Packaging" },
  { label: "Kilogram (kg)", value: "kg", category: "Weight" },
  { label: "Gram (g)", value: "g", category: "Weight" },
  { label: "Pound (lb)", value: "lb", category: "Weight" },
  { label: "Ounce (oz)", value: "oz", category: "Weight" },
  { label: "Litre (L)", value: "L", category: "Volume" },
  { label: "Millilitre (mL)", value: "mL", category: "Volume" },
  { label: "Metre (m)", value: "m", category: "Length" },
  { label: "Centimetre (cm)", value: "cm", category: "Length" },
];

const CATEGORY_COLORS = {
  Count: "blue",
  Packaging: "purple",
  Weight: "orange",
  Volume: "cyan",
  Length: "green",
};

const UOM_GROUPED = UOM_OPTIONS.reduce((acc, u) => {
  if (!acc[u.category]) acc[u.category] = [];
  acc[u.category].push(u);
  return acc;
}, {});

const sectionCardStyle = {
  borderRadius: 10,
  border: "1px solid #f0f0f0",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  marginBottom: 16,
};

const sectionTitleStyle = {
  fontWeight: 600,
  fontSize: 13,
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

function cbm(l, w, h) {
  if (!l || !w || !h) return null;
  return ((l * w * h) / 1_000_000).toFixed(4);
}

function weightLabel(kg) {
  if (!kg && kg !== 0) return null;
  if (kg === 0) return null;
  if (kg < 1) return { text: "Light", color: "green" };
  if (kg < 10) return { text: "Medium", color: "blue" };
  if (kg < 50) return { text: "Heavy", color: "orange" };
  return { text: "Very Heavy", color: "red" };
}

const LogisticsInformation = ({ form }) => {
  const [dims, setDims] = useState({ l: null, w: null, h: null });
  const [weight, setWeight] = useState(null);

  const handleUoMChange = (val) => {
    const purchaseUoM = form.getFieldValue("PurchaseUnitOfMeasure");
    if (!purchaseUoM) {
      form.setFieldValue("PurchaseUnitOfMeasure", val);
    }
  };

  const volume = cbm(dims.l, dims.w, dims.h);
  const wLabel = weightLabel(weight);

  return (
    <div className="col-12">
      {/* ── Units of Measurement ── */}
      <Card
        style={sectionCardStyle}
        styles={{ body: { padding: "16px 20px" } }}
        title={
          <span style={sectionTitleStyle}>
            <SwapOutlined style={{ color: "#1890ff" }} />
            Units of Measurement
          </span>
        }
      >
        <Row gutter={[16, 0]} align="bottom">
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              label={
                <span>
                  Unit of Measure&nbsp;
                  <Tooltip title="The unit used when selling this product (e.g. pcs, kg, box).">
                    <InfoCircleOutlined style={{ color: "#aaa", fontSize: 12 }} />
                  </Tooltip>
                </span>
              }
              name="UnitOfMeasure"
              rules={[{ required: true, message: "Please select a unit of measure" }]}
            >
              <Select
                placeholder="Select unit"
                allowClear
                onChange={handleUoMChange}
                showSearch
                optionFilterProp="label"
              >
                {Object.entries(UOM_GROUPED).map(([category, opts]) => (
                  <Select.OptGroup key={category} label={category}>
                    {opts.map((u) => (
                      <Option key={u.value} value={u.value} label={u.label}>
                        <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          {u.label}
                          <Tag color={CATEGORY_COLORS[u.category]} style={{ fontSize: 10, margin: 0 }}>
                            {u.category}
                          </Tag>
                        </span>
                      </Option>
                    ))}
                  </Select.OptGroup>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              label={
                <span>
                  Purchase Unit of Measure&nbsp;
                  <Tooltip title="The unit used when purchasing. Auto-filled from Unit of Measure — change only if it differs (e.g. buy in cartons, sell in pcs).">
                    <InfoCircleOutlined style={{ color: "#aaa", fontSize: 12 }} />
                  </Tooltip>
                </span>
              }
              name="PurchaseUnitOfMeasure"
            >
              <Select
                placeholder="Same as unit of measure"
                allowClear
                showSearch
                optionFilterProp="label"
              >
                {Object.entries(UOM_GROUPED).map(([category, opts]) => (
                  <Select.OptGroup key={category} label={category}>
                    {opts.map((u) => (
                      <Option key={u.value} value={u.value} label={u.label}>
                        <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          {u.label}
                          <Tag color={CATEGORY_COLORS[u.category]} style={{ fontSize: 10, margin: 0 }}>
                            {u.category}
                          </Tag>
                        </span>
                      </Option>
                    ))}
                  </Select.OptGroup>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={8}>
            <div
              style={{
                background: "#f6f8ff",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 24,
                fontSize: 12,
                color: "#666",
                lineHeight: 1.6,
              }}
            >
              <InfoCircleOutlined style={{ marginRight: 6, color: "#1890ff" }} />
              Set the <strong>Purchase UoM</strong> only when you buy in bulk units different from how you sell (e.g. buy by Carton, sell by Piece).
            </div>
          </Col>
        </Row>
      </Card>

      {/* ── Dimensions & Weight ── */}
      <Card
        style={sectionCardStyle}
        styles={{ body: { padding: "16px 20px" } }}
        title={
          <span style={sectionTitleStyle}>
            <BorderOutlined style={{ color: "#1890ff" }} />
            Dimensions &amp; Weight
          </span>
        }
      >
        <Row gutter={[16, 0]} align="bottom">
          {/* Weight */}
          <Col xs={24} sm={12} lg={6}>
            <Form.Item label="Weight" name="Weight">
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                placeholder="0.00"
                addonAfter="kg"
                onChange={(val) => setWeight(val)}
              />
            </Form.Item>
            {wLabel && (
              <div style={{ marginTop: -18, marginBottom: 12 }}>
                <Tag color={wLabel.color} style={{ fontSize: 11 }}>{wLabel.text}</Tag>
              </div>
            )}
          </Col>

          {/* Volume */}
          <Col xs={24} sm={12} lg={6}>
            <Form.Item label="Volume" name="Volume">
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                placeholder="0.00"
                addonAfter="L"
              />
            </Form.Item>
          </Col>

          {/* Dimensions group */}
          <Col xs={24} sm={24} lg={12}>
            <Form.Item label="Dimensions (L × W × H)">
              <Input.Group compact>
                <Form.Item name="Length" noStyle>
                  <InputNumber
                    min={0}
                    step={0.1}
                    placeholder="Length"
                    addonAfter="cm"
                    style={{ width: "33.3%" }}
                    onChange={(val) => setDims((d) => ({ ...d, l: val }))}
                  />
                </Form.Item>
                <Form.Item name="Width" noStyle>
                  <InputNumber
                    min={0}
                    step={0.1}
                    placeholder="Width"
                    addonAfter="cm"
                    style={{ width: "33.3%" }}
                    onChange={(val) => setDims((d) => ({ ...d, w: val }))}
                  />
                </Form.Item>
                <Form.Item name="Height" noStyle>
                  <InputNumber
                    min={0}
                    step={0.1}
                    placeholder="Height"
                    addonAfter="cm"
                    style={{ width: "33.4%" }}
                    onChange={(val) => setDims((d) => ({ ...d, h: val }))}
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>

            {/* Live CBM display */}
            <div
              style={{
                marginTop: -16,
                marginBottom: 12,
                minHeight: 24,
              }}
            >
              {volume ? (
                <Text style={{ fontSize: 12, color: "#888" }}>
                  Calculated volume:&nbsp;
                  <Text strong style={{ color: "#1890ff" }}>
                    {volume} m³
                  </Text>
                  &nbsp;
                  <Text style={{ fontSize: 11, color: "#aaa" }}>(CBM)</Text>
                </Text>
              ) : (
                <Text style={{ fontSize: 12, color: "#bbb" }}>
                  Enter L × W × H to see calculated volume (CBM)
                </Text>
              )}
            </div>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={16} lg={12}>
            <Form.Item label="Logistics Notes" name="LogisticsNotes">
              <Input.TextArea
                rows={3}
                placeholder="e.g. Handle with care, keep dry, stacking limit…"
                showCount
                maxLength={300}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default LogisticsInformation;
